/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { ProductAttributeV2Button } from '@/components/content/ProductDetails/parts/AttributeV2Button';
import { useProductDetails } from '@/data/Content/ProductDetails';
import { useProductDetailsTour } from '@/data/Content/ProductDetailsTour';
import { ContentContext } from '@/data/context/content';
import { ProductAttribute, Selection } from '@/data/types/Product';
import { Box, Skeleton, Stack, useMediaQuery, useTheme } from '@mui/material';
import { isEqual } from 'lodash';
import { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

const tallaOrder = {
	ech: 1,
	ch: 2,
	m: 3,
	g: 4,
	xg: 5,
};

const getFavoriteProductSelectSizeFromLocalStorage = () => {
	if (typeof window !== 'undefined') {
		const storedData = localStorage.getItem('favoriteProductSelectSize');
		return storedData ? JSON.parse(storedData) : null;
	}
	return null;
};

const removeFavoriteProductSelectSizeFromLocalStorage = () => {
	if (typeof window !== 'undefined') {
		localStorage.removeItem('favoriteProductSelectSize');
	}
};

type ProductAttributeV2Props = {
	attribute: ProductAttribute;
	skusAsAttrsList: Record<string, string>[];
	onAttributeChange: (attrId: string, valueId: string) => void;
	selection: Selection;
	isDrawer?: boolean;
	hasStock: (stock: boolean) => void;
	isPivot?: boolean;
};
export const ProductAttributeV2: FC<ProductAttributeV2Props> = ({
	attribute,
	skusAsAttrsList,
	onAttributeChange,
	selection,
	isPivot = false,
}) => {
	const {
		availability,
		sortedIdsWithSize,
		isLoadingInventory,
		sizeSelection,
		onAttributeChangeCustom,
	} = useContext(ContentContext) as ReturnType<typeof useProductDetails>;

	const { values, identifier: attrIdentifier } = attribute;
	const { attrsByIdentifier: currentlySelected = {} } = selection;
	const [active, setActive] = useState<string>('');
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const [isStock, setIsStock] = useState<boolean>();

	const { onSelectSizeProductDetailsTour } = useProductDetailsTour();

	const handleButtonClick = (optionValue: string) => {
		setActive(optionValue);
		onAttributeChange(attrIdentifier, optionValue);
		onSelectSizeProductDetailsTour(800);
	};

	const tallas = attribute['attribute.natural'];
	const identifierTalla: string[] = [];
	const valueTalla: string[] = [];

	values.forEach(({ identifier, value }) => {
		identifierTalla.push(identifier);
		valueTalla.push(value);
	});
	const allTallas = JSON.parse(tallas);

	allTallas.sort();
	identifierTalla.sort();
	valueTalla.sort();

	const initialAvailability = useRef(availability);

	useEffect(() => {
		if (!initialAvailability.current && availability) {
			initialAvailability.current = availability;
		}
		if (initialAvailability.current) {
			const stock = initialAvailability.current?.find((avial) => avial.status === true);
			setIsStock(!!stock);
		}
	}, [availability]);

	useEffect(() => {
		if (!initialAvailability.current && availability) {
			initialAvailability.current = availability;
		}
	}, [availability]);

	const sortByTalla = useCallback((tallaA: string, tallaB: string) => {
		const tallaALower = tallaA.toLowerCase();
		const tallaBLower = tallaB.toLowerCase();

		if (
			tallaOrder[tallaALower as keyof typeof tallaOrder] !== undefined &&
			tallaOrder[tallaBLower as keyof typeof tallaOrder] !== undefined
		) {
			return (
				tallaOrder[tallaALower as keyof typeof tallaOrder] -
				tallaOrder[tallaBLower as keyof typeof tallaOrder]
			);
		}

		const numA = parseFloat(tallaA);
		const numB = parseFloat(tallaB);

		if (!isNaN(numA) && !isNaN(numB)) {
			return numA - numB;
		}

		if (tallaOrder[tallaALower as keyof typeof tallaOrder] !== undefined) {
			return -1;
		}
		if (tallaOrder[tallaBLower as keyof typeof tallaOrder] !== undefined) {
			return 1;
		}

		return 0;
	}, []);

	const newAvailability = useMemo(
		() =>
			sortedIdsWithSize
				?.sort((a, b) => sortByTalla(a.talla as string, b.talla as string))
				?.map((item) => {
					const availabilityItem = availability?.find((avail) => avail.productId === item.id);
					return {
						...item,
						...availabilityItem,
					};
				}),
		[availability, sortedIdsWithSize, sortByTalla]
	);

	useEffect(() => {
		const favoriteProductSelectSize = getFavoriteProductSelectSizeFromLocalStorage();

		if (!selection?.attrsByIdentifier?.Talla && favoriteProductSelectSize && sizeSelection.loaded) {
			const product = newAvailability?.find(
				(item) =>
					item.talla?.toString().toLowerCase() ===
					favoriteProductSelectSize.toString().toLowerCase()
			);

			const talla = product?.talla?.toString();

			if (product && talla) {
				setActive(talla);
				onAttributeChangeCustom(attrIdentifier, talla);
			}
			removeFavoriteProductSelectSizeFromLocalStorage();
		}
	}, [selection, newAvailability, attrIdentifier, onAttributeChangeCustom, sizeSelection]);

	// Si no hay talla seleccionada y solo hay una talla, se selecciona la primera talla
	useEffect(() => {
		if (!selection?.attrsByIdentifier?.Talla && newAvailability?.length === 1) {
			const talla = newAvailability[0].talla?.toString();
			if (talla) {
				setActive(talla);
				onAttributeChangeCustom(attrIdentifier, talla);
			}
		}
	}, [selection, newAvailability, attrIdentifier, onAttributeChangeCustom]);

	return (
		<Stack spacing={1}>
			{!initialAvailability || isLoadingInventory || !availability || isStock === undefined ? (
				<Box
					sx={
						isMobile
							? {
									display: 'flex',
									overflowX: 'auto',
									whiteSpace: 'wrap',
									width: '100%',
									padding: '8px 0',
									'&::-webkit-scrollbar': {
										display: 'none',
									},
							  }
							: { display: 'flex', flexWrap: 'wrap' }
					}
				>
					{Array.from({ length: 6 }).map((_, index) => (
						<Skeleton
							key={index}
							variant="rectangular"
							sx={{
								marginRight: '2px',
								marginBottom: '2px',
								width: isMobile ? '68px' : '63px',
								height: '43px',
							}}
						/>
					))}
				</Box>
			) : (
				<Stack spacing={1} direction="row">
					<Box
						sx={{
							flexDirection: 'row',
							label: {
								marginRight: '0px',
								marginLeft: '0px',
							},
						}}
					>
						{isMobile ? (
							<Box
								sx={{
									display: 'flex',
									overflowX: 'auto',
									whiteSpace: 'wrap',
									width: '100%',
									padding: '8px 0',
									flexWrap: 'wrap',
									'&::-webkit-scrollbar': {
										display: 'none',
									},
								}}
							>
								{newAvailability?.map((talla: any, index: number) => {
									const selected = currentlySelected[attrIdentifier] === identifierTalla[index];
									const asSku = selected
										? null
										: { ...currentlySelected, [attrIdentifier]: identifierTalla[index] };
									const disabled =
										!selected && !skusAsAttrsList.find((sku) => isEqual(sku, asSku)) && !isPivot;

									if (talla.inventoryStatus === 'Unavailable') {
										return null;
									}

									if (isStock) {
										return (
											<ProductAttributeV2Button
												active={active}
												size={talla.talla}
												onClick={() => handleButtonClick(talla.talla)}
											/>
										);
									}
									return (
										<Box
											key={talla.partNumber}
											sx={{
												padding: '9px 16px',
												display: 'grid',
												placeItems: 'center',
												fontSize: '15px',
												border: '1px solid #C9C9C9',
												cursor: 'pointer !important',
												position: 'relative',
											}}
										>
											<Box
												sx={{
													position: 'absolute',
													width: '74px',
													height: '1px',
													backgroundColor: '#C9C9C9',
													transform: 'rotate(34deg)',
												}}
											></Box>
											{talla.talla}
										</Box>
									);
								})}
							</Box>
						) : (
							<Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
								{newAvailability?.map((talla: any, index: number) => {
									if (talla.inventoryStatus === 'Unavailable') {
										return null;
									}

									if (isStock) {
										return (
											<ProductAttributeV2Button
												active={active}
												size={talla.talla}
												onClick={() => handleButtonClick(talla.talla)}
											/>
										);
									}

									return (
										<Box
											key={talla.partNumber}
											sx={{
												padding: '9px 16px',
												width: '68px',
												display: 'grid',
												placeItems: 'center',
												fontSize: '15px',
												border: '1px solid #C9C9C9',
												cursor: 'pointer !important',
												position: 'relative',
											}}
										>
											<Box
												sx={{
													position: 'absolute',
													width: '77px',
													height: '1px',
													backgroundColor: '#C9C9C9',
													transform: 'rotate(31.5deg)',
												}}
											></Box>
											{talla.talla}
										</Box>
									);
								})}
							</Box>
						)}
					</Box>
				</Stack>
			)}
		</Stack>
	);
};
